import React, { useRef, useImperativeHandle, useMemo, useState } from 'react';
import { Card } from 'tea-component';
import Loading from '@gp/components/Loading';
import CoverUploader, { ICoverUploaderInstance } from '@gp/components/CoverUploader';
import DynamicForm from '@gp/components/DynamicForm';
import useProjectCover from '@gp/pages/Workspace/Product/components/GameOverview/_hooks/useProjectCover';
import useProjectForm from '@gp/pages/Workspace/Product/components/GameOverview/_hooks/useProjectForm';
import type { IBaseProps } from '@home/types/common';
import type { ICover } from '@gp/components/CoverUploader/types';
import type { ISubModuleInstance, IStudioDTO } from '@gp/pages/Workspace/Product/types';
import type { IProjectInformationVO } from './types';
// import { useMemoizedFn, useRequest, useDeepCompareEffect } from 'ahooks';

// @ts-ignore
import style from './index.module.scss';

interface IGameOverviewProps extends IBaseProps {
  onChange?: (payload: Record<string, any>) => void;
  footer?: React.ReactElement | null;
}

const GameOverview: React.ForwardRefRenderFunction<ISubModuleInstance, IGameOverviewProps> = (
  props,
  ref,
) => {
  const { onChange, footer } = props;
  // CoverUploader组件实例
  const coverUploader = useRef<ICoverUploaderInstance | null>(null);
  // cover上传的逻辑
  const {
    value: coverValue,
    setValue: setCoverValue,
    resetValue: resetCoverValue,
    onUploadSuccess: handleCoverUploadSuccess,
  } = useProjectCover();

  // information表单的逻辑
  const {
    fields: formFields,
    controls: formControls,
    validations: formValidations,
    value: formValue,
    setValue: setFormValue,
    resetValue: resetFormValue,
    onChange: handleFormChange,
    onSubmit: handleSubmit,
    fieldsBasic,
    fieldsTeam,
    fieldsGame,
    loading,
    engineOptional,
    setEngineOptional,
  } = useProjectForm();

  const handleCoverUploadSuccessInPage = (payload: ICover) => {
    onChange?.({ ...coverValue, ...payload, ...formValue });
    handleCoverUploadSuccess(payload);
  };

  const generateCustomizeAddOptionDataHandle = (value: string, text: string) => {
    setEngineOptional([...engineOptional, { id: value || '', tag: text || '' }]);
  };

  const generateCustomizeDeleteOptionDataHandle = (option: any) => {
    const newArray = engineOptional?.filter((item: any) => item.id !== option?.value);
    setEngineOptional(newArray);
  };

  const handleFormChangeInPage = (payload: IProjectInformationVO) => {
    onChange?.({ ...coverValue, ...formValue, ...payload });
    handleFormChange(payload);
  };

  useImperativeHandle(ref, () => ({
    initialize: (payload?: Record<string, any> | null) => {
      const coverValueTmp = {
        cover: payload?.cover || '',
        name: payload?.name || '',
        file_size: payload?.file_size ?? Number.NaN,
        timestamp: payload?.timestamp ?? Number.NaN,
      };

      const formValueTmp = {
        project: payload?.project || '',
        stage: payload?.stage || '',
        pro_coa: payload?.pro_coa || '',
        pro_coa_id: payload?.pro_coa_id || '',
        studio_entity_id: payload?.studio_entity_id || '',
        studio_location: payload?.studio_location || '',
        publisher: payload?.publisher || '',
        ip: payload?.ip || [],
        genre: payload?.genre || [],
        platform: payload?.platform || [],
        business_model: payload?.business_model || '',
        engine: payload?.business_model || [],

        // priority: payload?.priority || '',
        // group: {
        //   parent: payload?.group?.parent || '',
        //   child: payload?.group?.child || '',
        // },
        // form_2: payload?.form_2 || '',
        // expanded_genre: payload?.expanded_genre || '',
        // tag_line: payload?.tag_line || '',
        // ambition: payload?.ambition || '',
      };
      setCoverValue(coverValueTmp);
      setFormValue(formValueTmp);
    },
    submit: () => {
      const projectFormValue = handleSubmit();
      if (!projectFormValue) return null;
      return { ...projectFormValue, ...coverValue, engineOptional };
    },
    reset: () => {
      coverUploader.current?.reset();
      resetCoverValue();
      resetFormValue();
    },
  }));

  const fieldsList = useMemo(() => {
    return [
      { fields: fieldsBasic, title: '' },
      { fields: fieldsTeam, title: 'Team Information' },
      { fields: fieldsGame, title: 'Game Information' },
    ];
  }, [fieldsBasic, fieldsTeam, fieldsGame]);

  return (
    <Card className={`${style['game-overview-content']} dbg-add-product-game-overview-card`}>
      <Card.Body title="New Project">
        {loading && <Loading loading style={{ height: 260, padding: 10 }} />}
        {!loading && (
          <>
            <CoverUploader
              ref={coverUploader}
              value={coverValue}
              onUploadSuccess={handleCoverUploadSuccessInPage}
            />
            <DynamicForm
              formProps={{ className: 'gp-product-form font14', layout: 'fixed' }}
              formItemProps={{ className: 'gp-product-form-item' }}
              fields={formFields}
              controls={formControls}
              validations={formValidations}
              value={formValue}
              onChange={handleFormChangeInPage}
              isFieldsList={true}
              fieldsList={fieldsList}
              generateCustomizeAddOptionDataHandle={generateCustomizeAddOptionDataHandle}
              generateCustomizeDeleteOptionDataHandle={generateCustomizeDeleteOptionDataHandle}
            />
          </>
        )}
      </Card.Body>
      {!loading && React.isValidElement(footer) && <Card.Footer>{footer}</Card.Footer>}
    </Card>
  );
};

export default React.forwardRef(GameOverview);
